/**
 * Audit-Status-Dashboard.
 *
 * Live-Lauf der 3 statischen Audit-Scripts (API-Drift, Route-Permissions,
 * Backend-Permissions). Zeigt Gesamt-Score und einklappbare Drill-Down-
 * Karten pro Audit. Zielgruppe: Admins ohne CLI-Zugang.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { MaybeLayout } from '../components/layout/MaybeLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';
import {
  ShieldCheck, ShieldAlert, RefreshCw, CheckCircle2, XCircle,
  Activity, Network, Key, Eye,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const STAT_LABELS = {
  auth_dep: 'Standard Auth-Dep',
  inline_jwt: 'Inline JWT (Datei-Downloads)',
  token_pin: 'Token/PIN-Argument',
  whitelist: 'Whitelist (intentional public)',
  ungated: 'Ohne Gate ⚠️',
};

function StatusPill({ ok, count, testid }) {
  return (
    <Badge
      variant={ok ? 'secondary' : 'destructive'}
      className={`gap-1 ${ok ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' : ''}`}
      data-testid={testid}
    >
      {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
      {ok ? 'OK' : `${count} Findings`}
    </Badge>
  );
}

export default function AuditStatus({ embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/audit-status');
      setData(res.data);
    } catch (err) {
      toast.error('Audit konnte nicht geladen werden');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <MaybeLayout embedded={embedded}>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </MaybeLayout>
    );
  }
  if (!data) return null;

  const overallIcon = data.overall_ok ? ShieldCheck : ShieldAlert;
  const OverallIcon = overallIcon;

  return (
    <MaybeLayout embedded={embedded}>
      <div className="space-y-4 animate-fade-in" data-testid="audit-status-page">
        {/* Gesamt-Score */}
        <Card className={`border-2 ${data.overall_ok ? 'border-emerald-500/50 bg-emerald-50/50 dark:bg-emerald-950/20' : 'border-rose-500/50 bg-rose-50/50 dark:bg-rose-950/20'}`}>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <OverallIcon className={`h-10 w-10 ${data.overall_ok ? 'text-emerald-600' : 'text-rose-600'}`} />
              <div>
                <CardTitle className="font-heading text-2xl" data-testid="overall-status">
                  {data.overall_ok ? 'Alle Audits grün' : 'Audits melden Findings'}
                </CardTitle>
                <CardDescription>
                  Statisch analysiert — keine Laufzeit-Tests nötig.
                </CardDescription>
              </div>
            </div>
            <Button onClick={load} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4 mr-2" />
              Erneut prüfen
            </Button>
          </CardHeader>
        </Card>

        {/* Drei Audit-Karten als Accordion */}
        <Accordion type="multiple" defaultValue={data.overall_ok ? [] : ['api', 'routes', 'backend', 'nav']} className="space-y-2">
          {/* Audit 1: API-Drift */}
          <AccordionItem value="api" className="border rounded-lg bg-card" data-testid="audit-api-drift">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center justify-between w-full pr-4">
                <div className="flex items-center gap-3">
                  <Network className="h-5 w-5 text-primary" />
                  <div className="text-left">
                    <div className="font-semibold">API-Drift</div>
                    <div className="text-xs text-muted-foreground">
                      Frontend-Calls vs. Backend-Routen
                    </div>
                  </div>
                </div>
                <StatusPill
                  ok={data.api_drift.ok}
                  count={data.api_drift.missing.length + data.api_drift.method_drift.length}
                  testid="api-drift-pill"
                />
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="secondary">Frontend-Calls: <strong className="ml-1">{data.api_drift.frontend_calls_scanned}</strong></Badge>
                <Badge variant="secondary">Backend-Routen: <strong className="ml-1">{data.api_drift.backend_routes_loaded}</strong></Badge>
              </div>
              {data.api_drift.missing.length > 0 && (
                <div>
                  <div className="font-medium text-rose-700 dark:text-rose-400 text-sm mb-1">
                    Fehlende Endpoints ({data.api_drift.missing.length})
                  </div>
                  <ul className="space-y-1 text-xs font-mono">
                    {data.api_drift.missing.map((m, i) => (
                      <li key={i} className="text-muted-foreground">
                        <span className="font-semibold">{m.method}</span> {m.path} <span className="opacity-60">(api.js:{m.line})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.api_drift.method_drift.length > 0 && (
                <div>
                  <div className="font-medium text-amber-700 dark:text-amber-400 text-sm mb-1">
                    Method-Drift ({data.api_drift.method_drift.length})
                  </div>
                  <ul className="space-y-1 text-xs font-mono">
                    {data.api_drift.method_drift.map((m, i) => (
                      <li key={i}>
                        <span className="font-semibold">{m.method}</span> {m.path}
                        <span className="text-muted-foreground"> (Backend: {m.backend_methods.join(', ')})</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {data.api_drift.ok && (
                <p className="text-xs text-muted-foreground">
                  Alle Frontend-Calls finden ihre Backend-Endpoints.
                </p>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Audit 2: Route-Permissions */}
          <AccordionItem value="routes" className="border rounded-lg bg-card" data-testid="audit-routes">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center justify-between w-full pr-4">
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-primary" />
                  <div className="text-left">
                    <div className="font-semibold">Route ↔ Sidebar Konsistenz</div>
                    <div className="text-xs text-muted-foreground">
                      Permission-Gates synchron zwischen Routen und Navigation
                    </div>
                  </div>
                </div>
                <StatusPill
                  ok={data.route_permissions.ok}
                  count={data.route_permissions.findings.length}
                  testid="routes-pill"
                />
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-2">
              {data.route_permissions.findings.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  Alle Sidebar-Pfade haben passend gegated Routen — kein Direkt-URL-Bypass möglich.
                </p>
              ) : (
                <ul className="space-y-1 text-xs font-mono">
                  {data.route_permissions.findings.map((f, i) => (
                    <li key={i} className="text-rose-700 dark:text-rose-400">{f}</li>
                  ))}
                </ul>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Audit 3: Backend-Permissions */}
          <AccordionItem value="backend" className="border rounded-lg bg-card" data-testid="audit-backend">
            <AccordionTrigger className="px-4 py-3 hover:no-underline">
              <div className="flex items-center justify-between w-full pr-4">
                <div className="flex items-center gap-3">
                  <Key className="h-5 w-5 text-primary" />
                  <div className="text-left">
                    <div className="font-semibold">Backend Auth-Coverage</div>
                    <div className="text-xs text-muted-foreground">
                      Jeder Endpoint mit Auth-Gate oder explizit whitelisted
                    </div>
                  </div>
                </div>
                <StatusPill
                  ok={data.backend_permissions.ok}
                  count={data.backend_permissions.findings.length}
                  testid="backend-pill"
                />
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4 space-y-3">
              <div className="flex flex-wrap gap-2 text-xs">
                <Badge variant="secondary">
                  Endpoints gesamt: <strong className="ml-1">{data.backend_permissions.stats.total}</strong>
                </Badge>
                {Object.entries(data.backend_permissions.stats).filter(([k]) => k !== 'total').map(([k, v]) => (
                  <Badge
                    key={k}
                    variant={k === 'ungated' && v > 0 ? 'destructive' : 'secondary'}
                    className={k !== 'ungated' || v === 0 ? 'bg-muted/50' : ''}
                  >
                    {STAT_LABELS[k]}: <strong className="ml-1">{v}</strong>
                  </Badge>
                ))}
              </div>
              {data.backend_permissions.findings.length > 0 && (
                <div>
                  <div className="font-medium text-rose-700 dark:text-rose-400 text-sm mb-1">
                    Endpoints ohne Auth-Gate ({data.backend_permissions.findings.length})
                  </div>
                  <ul className="space-y-1 text-xs font-mono">
                    {data.backend_permissions.findings.map((f, i) => (
                      <li key={i} className="text-rose-700 dark:text-rose-400">{f}</li>
                    ))}
                  </ul>
                </div>
              )}
              {data.backend_permissions.ok && (
                <p className="text-xs text-muted-foreground">
                  Jeder Endpoint hat ein Auth-Gate oder ist explizit als public whitelisted.
                </p>
              )}
            </AccordionContent>
          </AccordionItem>

          {/* Audit 4: Nav ↔ Backend */}
          {data.nav_backend && (
            <AccordionItem value="nav" className="border rounded-lg bg-card" data-testid="audit-nav-backend">
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex items-center justify-between w-full pr-4">
                  <div className="flex items-center gap-3">
                    <Eye className="h-5 w-5 text-primary" />
                    <div className="text-left">
                      <div className="font-semibold">Sidebar ↔ Backend-Rechte</div>
                      <div className="text-xs text-muted-foreground">
                        Sichtbare Menüpunkte passen zu den tatsächlichen Backend-Berechtigungen
                      </div>
                    </div>
                  </div>
                  <StatusPill
                    ok={data.nav_backend.ok}
                    count={data.nav_backend.findings.length}
                    testid="nav-backend-pill"
                  />
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4 space-y-3">
                {data.nav_backend.findings.length > 0 && (
                  <ul className="space-y-1 text-xs font-mono">
                    {data.nav_backend.findings.map((f, i) => (
                      <li key={i} className="text-rose-700 dark:text-rose-400">{f}</li>
                    ))}
                  </ul>
                )}
                <div className="space-y-1">
                  {(data.nav_backend.rows || []).map((r) => (
                    <div key={r.path} className="flex items-center gap-2 text-xs" data-testid={`nav-backend-row-${r.path}`}>
                      {r.status === 'ok' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
                      {r.status === 'fail' && <XCircle className="h-3.5 w-3.5 text-rose-600 shrink-0" />}
                      {(r.status === 'warn' || r.status === 'uncovered') && <ShieldAlert className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                      <span className="font-mono font-semibold">{r.path}</span>
                      <Badge variant="secondary" className="bg-muted/50">{r.nav_gate || '—'}</Badge>
                      <span className="text-muted-foreground">{r.message}</span>
                    </div>
                  ))}
                </div>
                {data.nav_backend.uncovered?.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Ohne Backend-Mapping (manuell prüfen): {data.nav_backend.uncovered.join(', ')}
                  </p>
                )}
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </div>
    </MaybeLayout>
  );
}
